/*eslint max-lines-per-function: ["error", 250]*/
import React, { useState, useMemo, useEffect } from 'react';
import { Modal, Form, Tooltip, OverlayTrigger, Alert } from 'react-bootstrap';
import * as Icon from 'react-bootstrap-icons';

import { useNavigation } from '../../../common/navigation';
import { usePokCore } from '../../../common/hooks/usePokCore';
import {
  GridGetterFunction,
  GridCountFunction,
} from '../../../common/components/Grid/GridDataTypes';
import { useTranslation } from '../../../common/hooks/useTranslation';
import Button from '../../../common/components/Button/Button';
import { CreateUpdatePurchaseInvoiceDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import momencik from '../../../common/momencik';
import ConfirmationButton from '../../../app/components/ConfirmationButton';
import { useAuth } from '../../../common/hooks/useAuth';
import { PermissionsEnum } from '../../../common/pokCore/authorization/permissions';

import { PurchaseInvoiceSearch } from './PurchaseInvoiceSearch';

interface SelectOriginalInvoiceProps {
  originalInvoiceId?: string | null;
  clientId?: string;
  issueDate?: Date;
  currentInvoiceId?: string;
  currentInvoiceNumber?: string;
  readOnly: boolean;
  propertyChange: (obj: Partial<CreateUpdatePurchaseInvoiceDto>) => void;
}

export const SelectOriginalInvoice: React.FC<
  SelectOriginalInvoiceProps
> = props => {
  const pok = usePokCore();
  const { t, tk } = useTranslation('fvzLng');
  const [show, setShow] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState<string>('');
  const [selectedRow, setSelectedRow] = useState<string | null>(
    props.originalInvoiceId || null,
  );
  const [originalInvoiceId, setOriginalInvoiceId] = useState<
    string | null | undefined
  >(props.originalInvoiceId);

  const nav = useNavigation();
  const auth = useAuth();

  const isAdminOrFinanceUser = [
    PermissionsEnum.Finance,
    PermissionsEnum.Administration,
  ].some(permission => auth.check(permission, pok.getCompanyId()));

  const handleClose = () => {
    setShow(false);
  };

  const disabled = useMemo(() => {
    return !props.clientId;
  }, [props.clientId]);

  useEffect(() => {
    setOriginalInvoiceId(props.originalInvoiceId);
  }, [props.originalInvoiceId]);

  useEffect(() => {
    if (originalInvoiceId) {
      pok.purchaseInvoices
        .getById(originalInvoiceId)
        .then(invoice => {
          setInvoiceNumber(invoice.number);
        })
        .catch(() => {
          setInvoiceNumber('');
        });
    } else {
      setInvoiceNumber('');
    }
  }, [originalInvoiceId, pok.purchaseInvoices]);

  const getDataGenerated = useMemo(
    () =>
      ((
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
      ) =>
        pok.purchaseInvoices.getByClientAndDateByPortion(
          t,
          tk,
          pageSize,
          pageNumber,
          props.clientId,
          props.issueDate,
          props.currentInvoiceId,
          orderBy,
          orderDirection,
          filterText,
          filterColumns,
        )) as GridGetterFunction,
    [
      pok.purchaseInvoices,
      props.clientId,
      props.currentInvoiceId,
      props.issueDate,
      t,
      tk,
    ],
  );

  const getCount = useMemo(
    () =>
      ((filterText, filterColumns) =>
        pok.purchaseInvoices.getByClientAndDate(
          props.clientId,
          props.issueDate,
          props.currentInvoiceId,
          filterText,
          filterColumns,
        )) as GridCountFunction,
    [
      props.clientId,
      props.issueDate,
      props.currentInvoiceId,
      pok.purchaseInvoices,
    ],
  );

  const onRowClick = (selectedRow: string) => {
    setSelectedRow(selectedRow);
  };

  const onSelectInvoice = () => {
    if (selectedRow) {
      props.propertyChange({ originalInvoiceId: selectedRow });
      setOriginalInvoiceId(selectedRow);
      setShow(false);
    }
  };

  const onDelete = () => {
    setOriginalInvoiceId(undefined);
    props.propertyChange({ originalInvoiceId: null });
    setInvoiceNumber('');
    setSelectedRow(null);
  };

  const viewInvoice = () => {
    if (originalInvoiceId) {
      nav.purchaseInvoiceEditor(originalInvoiceId, true);
    }
  };

  return (
    <>
      <div className="d-flex">
        <div className="w-60 d-flex align-items-center">
          <Form.Control
            type="text"
            value={invoiceNumber || ''}
            disabled={true}
          />
          {isAdminOrFinanceUser && (
            <>
              <OverlayTrigger
                overlay={<Tooltip>{t(tk.finance.openNewWindow)}</Tooltip>}
              >
                <Button
                  onClick={() => {
                    viewInvoice();
                  }}
                  variant="outline-light"
                  disabled={!originalInvoiceId}
                >
                  <Icon.BoxArrowUpRight className="icon-blue" size={16} />
                </Button>
              </OverlayTrigger>

              <ConfirmationButton
                variant="outline-danger"
                confirmation={t(tk.finance.askDeleteOriginalInvoice)}
                tooltip={t(tk.finance.deleteOriginalInvoice)}
                onOK={() => onDelete()}
                disabled={!originalInvoiceId || props.readOnly}
              >
                <Icon.Trash />
              </ConfirmationButton>
            </>
          )}
        </div>
        <div className="w-40 ps-4">
          <div className="d-flex justify-content-end">
            {disabled ? (
              <OverlayTrigger
                overlay={<Tooltip>{t(tk.finance.selectClientBefore)}</Tooltip>}
              >
                <span className="w-100">
                  <Button
                    className="w-100"
                    onClick={() => setShow(true)}
                    variant="outline-success"
                    disabled={disabled}
                  >
                    {t(tk.finance.selectCorrectedInvoice)}
                  </Button>
                </span>
              </OverlayTrigger>
            ) : (
              <Button
                className="w-100"
                onClick={() => setShow(true)}
                variant="outline-success"
                disabled={props.readOnly}
              >
                {t(tk.finance.selectCorrectedInvoice)}
              </Button>
            )}
          </div>
        </div>
      </div>
      {show && (
        <Modal
          show={show}
          onHide={handleClose}
          centered
          keyboard={true}
          backdrop="static"
          backdropClassName="modal-on-modal-backdrop"
          className="modal-on-modal"
          dialogClassName="modal-90w"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <>
                {t(tk.finance.selectCorrectedInvoiceFor)}{' '}
                {props?.currentInvoiceNumber ? (
                  <>
                    {t(tk.finance.number)}{' '}
                    <strong>{props.currentInvoiceNumber}</strong>{' '}
                  </>
                ) : null}
                {props?.issueDate ? (
                  <>
                    {t(tk.finance.date)}{' '}
                    <strong>{momencik(props.issueDate)}</strong>
                  </>
                ) : null}
              </>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <PurchaseInvoiceSearch
              getData={getDataGenerated}
              getCount={getCount}
              onRowClick={onRowClick}
              withCountDays={true}
              displayCurrency={true}
              displayTeamsProjects={true}
              withFilterInColumns={true}
              showNewWindowButton={true}
              enableSingleSelect={true}
              selectedKeys={[selectedRow || '']}
            />
            <Alert variant="info">
              {t(tk.finance.warningSelectCorrectedInvoice)}
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={handleClose}>
              {t(tk.button.close)}
            </Button>
            <Button variant="primary" onClick={onSelectInvoice}>
              {t(tk.button.select)}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};
